import type {
  CarnivalAction,
  CarnivalGameState,
  CarnivalViewerState,
} from "@cloudnest/carnival-of-sins-engine";
import {
  filterStateForViewer,
  validateAndReduceGameState,
} from "@cloudnest/carnival-of-sins-engine";
import { carnivalOfSinsEventToAction } from "./event-adapter";
import type { CarnivalOfSinsReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free Carnival of Sins replay simulation. `init` seeds canonical state;
 * every other event is reconstructed via the event adapter and folded
 * through the engine reducer. Fails loudly on unsupported/rejected events.
 *
 * Each snapshot carries both the canonical `state` (for the post-game recap
 * toggle) and an observer `viewer` projection (`filterStateForViewer(state,
 * null)`) where a not-yet-revealed pending selection is masked. The replay
 * screen renders `viewer` by default so no private value reaches the DOM
 * during step-through.
 */
export interface CarnivalOfSinsReplaySnapshot {
  readonly index: number;
  readonly event: CarnivalOfSinsReplayFixtureEvent;
  readonly action: CarnivalAction | null;
  readonly state: CarnivalGameState;
  readonly viewer: CarnivalViewerState;
}

function requireState(
  state: CarnivalGameState | null,
  eventType: string,
): CarnivalGameState {
  if (state === null) throw new Error(`${eventType} before init`);
  return state;
}

function snapshot(
  index: number,
  event: CarnivalOfSinsReplayFixtureEvent,
  action: CarnivalAction | null,
  state: CarnivalGameState,
): CarnivalOfSinsReplaySnapshot {
  return {
    index,
    event,
    action,
    state,
    viewer: filterStateForViewer(state, null),
  };
}

export function buildCarnivalOfSinsReplaySnapshots(
  events: readonly CarnivalOfSinsReplayFixtureEvent[],
): readonly CarnivalOfSinsReplaySnapshot[] {
  const snapshots: CarnivalOfSinsReplaySnapshot[] = [];
  let state: CarnivalGameState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = (event.payload as { state: CarnivalGameState }).state;
      snapshots.push(snapshot(snapshots.length, event, null, state));
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = carnivalOfSinsEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push(snapshot(snapshots.length, event, action, state));
  }

  if (state === null) {
    throw new Error("Cannot replay an empty carnival-of-sins event log");
  }
  return snapshots;
}
